import { ApolloClient, InMemoryCache, createHttpLink, fromPromise } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { isIDToken } from '@okta/okta-auth-js';
import oktaAuth from './okta.provider';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_ENDPOINT_URL,
});

const authLink = setContext(async (_, { headers }) => {
  let token = oktaAuth.getIdToken();

  try {
    const tokens = await oktaAuth.tokenManager.get('idToken');
    if (tokens && isIDToken(tokens)) {
      token = tokens.idToken;
    }
  } catch {
    // Fall back to cached token
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const errorLink = onError(({ networkError, operation, forward }) => {
  const statusCode = (networkError as any)?.statusCode;
  const errorMessage = (networkError as any)?.result?.message;

  if (statusCode === 401 || errorMessage === 'Unauthorized') {
    return fromPromise(
      oktaAuth.tokenManager.renew('idToken').catch(() => {
        oktaAuth.signInWithRedirect({ originalUri: window.location.pathname });
        return null;
      }),
    ).flatMap((renewed) => {
      if (!renewed || !isIDToken(renewed)) return forward(operation);

      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${renewed.idToken}`,
        },
      }));

      return forward(operation);
    });
  }
});

const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  cache: new InMemoryCache({
    typePolicies: {
      Table: {
        keyFields: ['id', 'floorId'],
      },
      Workstation: {
        keyFields: ['id', 'tableId', 'floorId'],
      },
      Reservation: {
        fields: {
          workstationreservation: {
            merge: false,
          },
        },
      },
      Query: {
        fields: {
          getAllReservations: {
            merge: false,
          },
          getReservationByDateAndFloor: {
            merge: false,
          },
        },
      },
    },
  }),
});

export default apolloClient;
